import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, FormControl, Select, MenuItem, InputLabel,
  Grid, Stepper, Step, StepLabel, FormHelperText, Alert
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { getReferences } from '../../services/SuppliesService';
import { getCategoriesByType } from '../../services/CategoryService';
import { addPreparation, updatePreparation } from '../../services/PreparationService';
import { unitOptions, categoryOptions } from '../../utils/Enums';
import ProductItemManager from '../../components/ProductItem/ProductItemManager';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import CurrencySelect from '../Currency/CurrencySelect';
import { DEFAULT_CURRENCY } from '../../utils/currency';

const AddEditPreparationModal = ({ locationId, open, onClose, onSave, preparation }) => {
  const { t } = useTranslation();
  const itemsManagerRef = useRef();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [references, setReferences] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    preparationType: 'DEFINED',
    unit: '',
    categoryId: '',
    subCategoryId: '',
    stock: 0,
    suggestedStock: 0,
    minimumStock: 0,
    batch: 0,
    costCurrency: DEFAULT_CURRENCY,
    items: []
  });

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    setDependenciesLoading(true);

    Promise.allSettled([
      getCategoriesByType(categoryOptions.PREPARATIONS),
      getReferences(locationId)
    ]).then(([categoriesResult, referencesResult]) => {
      if (!isMounted) return;

      const fetchedCategories = categoriesResult.status === 'fulfilled' ? (categoriesResult.value || []) : [];
      const fetchedReferences = referencesResult.status === 'fulfilled' ? (referencesResult.value || []) : [];
      setCategories(fetchedCategories);
      setReferences(fetchedReferences);

      if (categoriesResult.status === 'rejected') {
        console.error('Error loading preparation categories:', categoriesResult.reason);
      }
      if (referencesResult.status === 'rejected') {
        console.error('Error loading preparation references:', referencesResult.reason);
      }

      if (preparation) {
        setFormData({
          name: preparation.name,
          preparationType: preparation.preparationType || 'DEFINED',
          unit: preparation.unit,
          categoryId: preparation.category?.id || '',
          subCategoryId: preparation.subCategory?.id || '',
          stock: preparation.stock || 0,
          suggestedStock: preparation.suggestedStock || 0,
          minimumStock: preparation.minimumStock || 0,
          batch: preparation.batch || 0,
          costCurrency: preparation.preferredCostCurrency || DEFAULT_CURRENCY,
          items: preparation.items.map(item => ({
            referenceId1: item.referenceId,
            referenceType: item.referenceType,
            quantity: item.quantity,
            unit: item.unit
          }))
        });
      } else {
        resetForm();
      }
    }).finally(() => {
      if (isMounted) setDependenciesLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [open, preparation]);

  const resetForm = () => {
    setFormData({
      name: '',
      preparationType: 'DEFINED',
      unit: '',
      categoryId: '',
      subCategoryId: '',
      stock: 0,
      suggestedStock: 0,
      minimumStock: 0,
      batch: 0,
      costCurrency: DEFAULT_CURRENCY,
      items: []
    });
  };

  const steps = useMemo(() => [t('basic_information'), t('preparation_list')], [t]);

  const dependencyWarnings = useMemo(() => {
    if (preparation || dependenciesLoading) return [];

    const warnings = [];
    const hasCategories = categories.length > 0;
    const hasSubCategories = categories.some(category => (category.subCategories || []).length > 0);

    if (!hasCategories) {
      warnings.push('Antes de crear una preparación es necesario crear una categoría.');
    } else if (!hasSubCategories) {
      warnings.push('Antes de crear una preparación es necesario crear una subcategoría dentro de una categoría.');
    }

    return warnings;
  }, [categories, dependenciesLoading, preparation]);

  const hasBlockingDependencies = dependencyWarnings.length > 0;

  const handleNext = () => {
    if (hasBlockingDependencies) return;

    let isValid = true;
    let errors = {};

    if (activeStep === 0) {
      if (!formData.name?.trim()) {
          errors.name = t('name_required');
          isValid = false;
      }
      if (!formData.unit) {
          errors.unit = t('unit_required');
          isValid = false;
      }
      if (!formData.batch || formData.batch <= 0) {
          errors.batch = t('batch_required');
          isValid = false;
      }
      if (!formData.categoryId) {
          errors.categoryId = t('category_required');
          isValid = false;
      }
      if (!formData.subCategoryId) {
          errors.subCategoryId = t('subcategory_required');
          isValid = false;
      }
      if (formData.stock === '' || formData.stock < 0) {
          errors.stock = t('stock_required');
          isValid = false;
      }
      if (formData.suggestedStock === '' || formData.suggestedStock < 0) {
          errors.suggestedStock = t('suggested_stock_required');
          isValid = false;
      }
      if (formData.minimumStock === '' || formData.minimumStock < 0) {
          errors.minimumStock = t('minimum_stock_required');
          isValid = false;
      }

      setFormErrors(errors);

      console.log(errors)

      if (!isValid) return;
    } else {
      if (!itemsManagerRef.current?.validateItems()) {
          return;
      }
    }

    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleCategoryChange = (event) => {
    setFormData({ ...formData, categoryId: event.target.value, subCategoryId: '' });
  };

  const handleSubCategoryChange = (event) => {
    setFormData({ ...formData, subCategoryId: event.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let response = preparation
        ? await updatePreparation(preparation.id, formData, locationId)
        : await addPreparation(formData, locationId);

      onSave(response);
      onClose();
    } catch (error) {
      console.error('Error saving preparation:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{preparation ? t('edit_preparation') : t('add_preparation')}</DialogTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Stepper activeStep={activeStep} sx={{ width: '800px', my: 2 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <DialogContent>
        {activeStep === 0 && dependencyWarnings.map((warning) => (
          <Alert key={warning} severity="error" sx={{ mb: 2 }}>
            {warning}
          </Alert>
        ))}
        {activeStep === 0 && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('name')}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.unit}>
                  <InputLabel>{t('unit')}</InputLabel>
                  <Select
                    label={t('unit')}
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  >
                    {unitOptions.map(option => (
                      <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formErrors.unit}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('product_type') || 'Tipo'}</InputLabel>
                  <Select
                    label={t('product_type') || 'Tipo'}
                    value={formData.preparationType}
                    onChange={(e) => setFormData({ ...formData, preparationType: e.target.value })}
                  >
                    <MenuItem value="DEFINED">{t('product_type_defined') || 'Definido'}</MenuItem>
                    <MenuItem value="UNDEFINED">{t('product_type_undefined') || 'Indefinido'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={t('product_type_tooltip') || "DEFINED: Producto ya definido/preparado (descuenta stock del producto). UNDEFINED: Producto no definido hasta la venta (descuenta ingredientes). RETAIL: Producto comprado y vendido directamente (descuenta stock del producto)."}
                  arrow
                  placement="right"
                >
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batch')}
                  type="number"
                  error={!!formErrors.batch}
                  helperText={formErrors.batch}
                  value={formData.batch}
                  onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CurrencySelect
                  label={t('cost_currency') || 'Moneda del costo'}
                  value={formData.costCurrency}
                  onChange={(costCurrency) => setFormData({ ...formData, costCurrency })}
                  size="medium"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{marginTop: '6px'}}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.categoryId}>
                  <InputLabel>{t('category')}</InputLabel>
                  <Select value={formData.categoryId} onChange={handleCategoryChange} label={t('category')}>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{formErrors.categoryId}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.subCategoryId} disabled={!formData.categoryId}>
                  <InputLabel>{t('sub_category')}</InputLabel>
                  <Select value={formData.subCategoryId} onChange={handleSubCategoryChange} label={t('sub_category')}>
                    {categories.find(cat => cat.id === formData.categoryId)?.subCategories.map(subCategory => (
                      <MenuItem key={subCategory.id} value={subCategory.id}>{subCategory.name}</MenuItem>
                    )) || []}
                  </Select>
                  <FormHelperText>{formErrors.subCategoryId}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('stock')}
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  error={!!formErrors.stock}
                  helperText={formErrors.stock}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('minimum_stock')}
                  type="number"
                  value={formData.minimumStock}
                  onChange={(e) => setFormData({ ...formData, minimumStock: e.target.value })}
                  error={!!formErrors.minimumStock}
                  helperText={formErrors.minimumStock}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('suggested_stock')}
                  type="number"
                  value={formData.suggestedStock}
                  onChange={(e) => setFormData({ ...formData, suggestedStock: e.target.value })}
                  error={!!formErrors.suggestedStock}
                  helperText={formErrors.suggestedStock}
                  fullWidth
                />
              </Grid>
            </Grid>
          </>
        )}

        {activeStep === 1 && (
          <ProductItemManager
            ref={itemsManagerRef}
            items={formData.items}
            setItems={(newItems) => setFormData({ ...formData, items: newItems })}
            data={references}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={activeStep === 0} onClick={handleBack}>{t('back')}</Button>
        <LoadingButton
          loading={loading}
          disabled={activeStep === 0 && hasBlockingDependencies}
          onClick={() => {
            if (activeStep === steps.length - 1) {
              handleNext(); // Primero ejecutamos la validación
              if (itemsManagerRef.current?.validateItems()) {
                handleSave(); // Solo guarda si `validateItems()` es válido
              }
            } else {
              handleNext();
            }
          }}
          variant="contained"
        >
          {activeStep === steps.length - 1 ? t('save') : t('next')}
        </LoadingButton>

      </DialogActions>
    </Dialog>
  );
};

export default AddEditPreparationModal;
